import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthSession, selfHelpApi, fileUploadRequest, getMediaUrl } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';

const TARGET_AUDIENCES = ['All Ages', 'Adults', 'Children / Kids', 'Teens & Adolescents', 'Parents & Families'];

const PRESET_AUDIO_IMAGES = [
  {
    label: '🌙 Deep Sleep',
    url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '🌊 Ocean Calm',
    url: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '🌲 Forest Breath',
    url: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '☀️ Sunrise Energy',
    url: 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '🎯 Zen Focus',
    url: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '🍃 Serene Lake',
    url: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '💜 Self-Compassion',
    url: 'https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=900&q=80',
  },
  {
    label: '🧘 Sanctuary',
    url: '/assets/images/auth-soothing-wellness.jpg',
  },
];

const getDefaultCategoryPreview = (name = '', idx = 0) => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('sleep') || lower.includes('night')) return PRESET_AUDIO_IMAGES[0].url;
  if (lower.includes('anxiet') || lower.includes('panic')) return PRESET_AUDIO_IMAGES[1].url;
  if (lower.includes('breath') || lower.includes('ground')) return PRESET_AUDIO_IMAGES[2].url;
  if (lower.includes('morning') || lower.includes('energy') || lower.includes('positiv')) return PRESET_AUDIO_IMAGES[3].url;
  if (lower.includes('focus') || lower.includes('study') || lower.includes('work')) return PRESET_AUDIO_IMAGES[4].url;
  if (lower.includes('stress') || lower.includes('relax')) return PRESET_AUDIO_IMAGES[5].url;
  if (lower.includes('self') || lower.includes('love') || lower.includes('compassion')) return PRESET_AUDIO_IMAGES[6].url;
  return PRESET_AUDIO_IMAGES[idx % PRESET_AUDIO_IMAGES.length].url;
};

const AdminSelfHelpPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSectionTab, setActiveSectionTab] = useState('audio'); // 'audio' | 'podcast' | 'activity' | 'all'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // Currently playing audio in admin table
  const [playingAudioId, setPlayingAudioId] = useState(null);

  // Modal State for Create / Edit Resource
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Modal State for Creating / Editing a Category
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newCategoryImageUrl, setNewCategoryImageUrl] = useState('');
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Multiple Audio Upload States
  const [showBulkAudioModal, setShowBulkAudioModal] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [isNewBulkCategory, setIsNewBulkCategory] = useState(false);
  const [newBulkCategoryName, setNewBulkCategoryName] = useState('');
  const [bulkNarrator, setBulkNarrator] = useState('');
  const [bulkSummary, setBulkSummary] = useState('');
  const [bulkThumbnailUrl, setBulkThumbnailUrl] = useState('');
  const [uploadingBulkThumbnail, setUploadingBulkThumbnail] = useState(false);
  const [bulkTracks, setBulkTracks] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState('');

  // Upload States
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingWorksheet, setUploadingWorksheet] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  const [audioUploadError, setAudioUploadError] = useState('');
  const [pdfUploadError, setPdfUploadError] = useState('');
  const [worksheetUploadError, setWorksheetUploadError] = useState('');
  const [thumbnailUploadError, setThumbnailUploadError] = useState('');

  // Form Data
  const defaultFormData = {
    section: 'audio', // 'audio' | 'podcast' | 'activity'
    title: '',
    category: '',
    summary: '',
    content: '',
    // Audio specific
    audioUrl: '',
    duration: '',
    narrator: '',
    // Podcast specific
    pdfUrl: '',
    pdfFileName: '',
    externalLink: '',
    // Activity specific
    worksheetFileUrl: '',
    worksheetFileName: '',
    targetAudience: 'All Ages',
    instructions: '',
    // Common
    thumbnailUrl: '',
    isPublished: true,
  };

  const [formData, setFormData] = useState(defaultFormData);

  // Fetch dynamic resources and categories from backend
  const fetchAllData = async () => {
    setLoading(true);
    setError('');
    try {
      const [resData, catData] = await Promise.all([
        selfHelpApi.adminGetResources(),
        selfHelpApi.getCategories({ section: 'audio' }),
      ]);
      setResources(resData.resources || []);
      setCategories(catData.categories || []);
    } catch (err) {
      console.error('Failed to fetch self-help admin data:', err);
      setError(err.message || 'Failed to load self-help resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchAllData();
  }, [authSession?.token, navigate]);

  // Open Create Modal for specific section
  const handleOpenCreateModal = (targetSection = 'audio') => {
    setEditingItem(null);
    setAudioUploadError('');
    setPdfUploadError('');
    setWorksheetUploadError('');
    setThumbnailUploadError('');

    const defaultCategory =
      targetSection === 'audio'
        ? categories[0]?.name || 'General Audio'
        : targetSection === 'podcast'
          ? 'Mental Wellness Podcast'
          : 'CBT Worksheet';

    setFormData({
      ...defaultFormData,
      section: targetSection,
      category: defaultCategory,
    });
    setShowModal(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item) => {
    setEditingItem(item);
    setAudioUploadError('');
    setPdfUploadError('');
    setWorksheetUploadError('');
    setThumbnailUploadError('');

    const sec = item.section || (item.type === 'worksheet' ? 'activity' : item.type === 'guide' ? 'podcast' : 'audio');

    setFormData({
      section: sec,
      title: item.title || '',
      category: item.category || (categories[0]?.name || 'General'),
      summary: item.summary || '',
      content: item.content || '',
      audioUrl: item.audioUrl || item.mediaUrl || '',
      duration: item.duration || item.readTime || '',
      narrator: item.narrator || '',
      pdfUrl: item.pdfUrl || '',
      pdfFileName: item.pdfFileName || '',
      externalLink: item.externalLink || '',
      worksheetFileUrl: item.worksheetFileUrl || '',
      worksheetFileName: item.worksheetFileName || '',
      targetAudience: item.targetAudience || 'All Ages',
      instructions: item.instructions || '',
      thumbnailUrl: item.thumbnailUrl || item.imageUrl || '',
      isPublished: item.isPublished !== undefined ? item.isPublished : true,
    });
    setShowModal(true);
  };

  // Open Create Category Modal
  const handleOpenCreateCategoryModal = () => {
    setEditingCategory(null);
    setNewCategoryName('');
    setNewCategoryDescription('');
    setNewCategoryImageUrl('');
    setShowCategoryModal(true);
  };

  // Open Edit Category Modal (for setting/changing category cover image & details)
  const handleOpenEditCategoryModal = (cat, idx = 0) => {
    setEditingCategory(cat);
    setNewCategoryName(cat.name || '');
    setNewCategoryDescription(cat.description || '');
    setNewCategoryImageUrl(cat.imageUrl || cat.thumbnailUrl || getDefaultCategoryPreview(cat.name, idx));
    setShowCategoryModal(true);
  };

  // Upload Category Cover Image file
  const handleCategoryImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCategoryImage(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setNewCategoryImageUrl(res.url);
      } else {
        throw new Error(res?.message || 'Cover image upload failed');
      }
    } catch (err) {
      alert(err.message || 'Failed to upload category cover image');
    } finally {
      setUploadingCategoryImage(false);
      e.target.value = '';
    }
  };

  // Upload Bulk Audio Default Cover Image file
  const handleBulkThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBulkThumbnail(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setBulkThumbnailUrl(res.url);
      } else {
        throw new Error(res?.message || 'Cover image upload failed');
      }
    } catch (err) {
      alert(err.message || 'Failed to upload cover image');
    } finally {
      setUploadingBulkThumbnail(false);
      e.target.value = '';
    }
  };

  // Create or Update category dynamically
  const handleCreateCategorySubmit = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      const payload = {
        name: newCategoryName.trim(),
        section: 'audio',
        description: newCategoryDescription.trim(),
        imageUrl: newCategoryImageUrl.trim(),
        thumbnailUrl: newCategoryImageUrl.trim(),
      };
      if (editingCategory && editingCategory._id) {
        await selfHelpApi.adminUpdateCategory(editingCategory._id, payload);
        alert(`Category "${newCategoryName}" updated successfully!`);
      } else {
        await selfHelpApi.adminCreateCategory(payload);
        alert(`Category "${newCategoryName}" created successfully!`);
      }
      setEditingCategory(null);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setNewCategoryImageUrl('');
      setShowCategoryModal(false);
      fetchAllData();
    } catch (err) {
      alert(err.message || 'Failed to save category');
    } finally {
      setCreatingCategory(false);
    }
  };

  // Open Multiple Audio Upload Modal
  const handleOpenBulkAudioModal = (preselectedCategory = '') => {
    let targetCat = preselectedCategory;
    if (!targetCat && filterCategory !== 'all') {
      targetCat = filterCategory;
    }
    if (!targetCat) {
      targetCat = categories[0]?.name || 'Deep Sleep';
    }

    const matchedCat = categories.find((c) => c.name === targetCat);
    setBulkCategory(targetCat);
    setIsNewBulkCategory(false);
    setNewBulkCategoryName('');
    setBulkNarrator('');
    setBulkSummary('');
    setBulkThumbnailUrl(matchedCat?.imageUrl || matchedCat?.thumbnailUrl || '');
    setBulkTracks([]);
    setBulkError('');
    setShowBulkAudioModal(true);
  };

  // Handle Selection of Multiple Audio Files
  const handleBulkAudioFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBulkError('');

    const newTrackPlaceholders = files.map((file, idx) => {
      const id = `track_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 5)}`;
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      return {
        id,
        file,
        fileName: file.name,
        fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        title: cleanName,
        duration: '',
        narrator: bulkNarrator || '',
        summary: bulkSummary || '',
        audioUrl: '',
        status: 'uploading',
        errorMsg: '',
      };
    });

    setBulkTracks((prev) => [...prev, ...newTrackPlaceholders]);

    // Inspect duration & upload each file
    for (const track of newTrackPlaceholders) {
      try {
        const objectUrl = URL.createObjectURL(track.file);
        const tempAudio = new Audio(objectUrl);
        tempAudio.onloadedmetadata = () => {
          const sec = Math.round(tempAudio.duration);
          if (!isNaN(sec) && sec > 0) {
            const min = Math.floor(sec / 60);
            const remSec = sec % 60;
            const durationStr = `${min}:${remSec < 10 ? '0' : ''}${remSec}`;
            setBulkTracks((prev) =>
              prev.map((t) => (t.id === track.id ? { ...t, duration: durationStr } : t))
            );
          }
          URL.revokeObjectURL(objectUrl);
        };
      } catch (e) {
        // Ignored
      }

      try {
        const data = new FormData();
        data.append('file', track.file);
        const res = await fileUploadRequest('/upload/file', data);
        if (res?.url) {
          setBulkTracks((prev) =>
            prev.map((t) => (t.id === track.id ? { ...t, audioUrl: res.url, status: 'ready' } : t))
          );
        } else {
          throw new Error(res?.message || 'Upload failed');
        }
      } catch (err) {
        console.error('Bulk upload track error:', err);
        setBulkTracks((prev) =>
          prev.map((t) => (t.id === track.id ? { ...t, status: 'error', errorMsg: err.message || 'Upload failed' } : t))
        );
      }
    }

    e.target.value = '';
  };

  // Remove track from bulk list
  const handleRemoveBulkTrack = (trackId) => {
    setBulkTracks((prev) => prev.filter((t) => t.id !== trackId));
  };

  // Update track property in bulk list
  const handleUpdateBulkTrack = (trackId, field, value) => {
    setBulkTracks((prev) =>
      prev.map((t) => (t.id === trackId ? { ...t, [field]: value } : t))
    );
  };

  // Submit all bulk audio tracks
  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setBulkError('');

    const targetCategory = isNewBulkCategory ? newBulkCategoryName.trim() : bulkCategory.trim();
    if (!targetCategory) {
      setBulkError('Please select or specify an audio category.');
      return;
    }

    if (bulkTracks.length === 0) {
      setBulkError('Please select at least one audio file to upload.');
      return;
    }

    const stillUploading = bulkTracks.some((t) => t.status === 'uploading');
    if (stillUploading) {
      setBulkError('Please wait for all audio files to finish uploading.');
      return;
    }

    const readyTracks = bulkTracks.filter((t) => t.status === 'ready' && t.audioUrl);
    if (readyTracks.length === 0) {
      setBulkError('No audio files were successfully uploaded. Please verify and try again.');
      return;
    }

    setBulkSaving(true);
    try {
      const payload = {
        category: targetCategory,
        narrator: bulkNarrator,
        thumbnailUrl: bulkThumbnailUrl.trim(),
        imageUrl: bulkThumbnailUrl.trim(),
        items: readyTracks.map((t) => ({
          title: t.title || t.fileName,
          audioUrl: t.audioUrl,
          duration: t.duration,
          narrator: t.narrator || bulkNarrator,
          summary: t.summary || bulkSummary,
          thumbnailUrl: t.thumbnailUrl || bulkThumbnailUrl.trim(),
          imageUrl: t.thumbnailUrl || bulkThumbnailUrl.trim(),
          isPublished: true,
        })),
      };

      const res = await selfHelpApi.adminBulkCreateAudios(payload);
      alert(`✓ ${res.message || `Successfully uploaded ${readyTracks.length} audios under "${targetCategory}"!`}`);
      setShowBulkAudioModal(false);
      setBulkTracks([]);
      await fetchAllData();
      setActiveSectionTab('audio');
      setFilterCategory(targetCategory);
    } catch (err) {
      console.error('Bulk audio save error:', err);
      setBulkError(err.message || 'Failed to save audio resources');
    } finally {
      setBulkSaving(false);
    }
  };

  // Switch Section inside Modal
  const handleSectionChange = (newSec) => {
    let newCat = categories[0]?.name || 'General Audio';
    if (newSec === 'podcast') newCat = 'Mental Wellness Podcast';
    if (newSec === 'activity') newCat = 'CBT Worksheet';

    setFormData((prev) => ({
      ...prev,
      section: newSec,
      category: newCat,
    }));
  };

  // Upload Audio File
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudioUploadError('');
    setUploadingAudio(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({
          ...prev,
          audioUrl: res.url,
          mediaUrl: res.url,
        }));
      } else {
        throw new Error(res?.message || 'Audio upload failed');
      }
    } catch (err) {
      console.error('Audio upload error:', err);
      setAudioUploadError(err.message || 'Failed to upload audio file');
    } finally {
      setUploadingAudio(false);
      e.target.value = '';
    }
  };

  // Upload PDF (for Podcast / Resource)
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploadError('');
    setUploadingPdf(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({
          ...prev,
          pdfUrl: res.url,
          pdfFileName: file.name,
        }));
      } else {
        throw new Error(res?.message || 'PDF upload failed');
      }
    } catch (err) {
      console.error('PDF upload error:', err);
      setPdfUploadError(err.message || 'Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
      e.target.value = '';
    }
  };

  // Upload Worksheet File (for Activities)
  const handleWorksheetUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setWorksheetUploadError('');
    setUploadingWorksheet(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({
          ...prev,
          worksheetFileUrl: res.url,
          worksheetFileName: file.name,
        }));
      } else {
        throw new Error(res?.message || 'Worksheet upload failed');
      }
    } catch (err) {
      console.error('Worksheet upload error:', err);
      setWorksheetUploadError(err.message || 'Failed to upload worksheet');
    } finally {
      setUploadingWorksheet(false);
      e.target.value = '';
    }
  };

  // Upload Thumbnail Image
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailUploadError('');
    setUploadingThumbnail(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await fileUploadRequest('/upload/file', data);
      if (res?.url) {
        setFormData((prev) => ({ ...prev, thumbnailUrl: res.url }));
      } else {
        throw new Error(res?.message || 'Thumbnail upload failed');
      }
    } catch (err) {
      console.error('Thumbnail upload error:', err);
      setThumbnailUploadError(err.message || 'Failed to upload thumbnail');
    } finally {
      setUploadingThumbnail(false);
      e.target.value = '';
    }
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.title.trim()) {
        throw new Error('Please enter a title');
      }

      if (!formData.category.trim()) {
        throw new Error('Please enter or select a category');
      }

      const payload = {
        ...formData,
        imageUrl: formData.thumbnailUrl || '',
        mediaUrl: formData.audioUrl || formData.worksheetFileUrl || formData.pdfUrl || '',
      };

      if (editingItem) {
        await selfHelpApi.adminUpdateResource(editingItem._id || editingItem.id, payload);
        alert('Self-Help item updated successfully!');
      } else {
        await selfHelpApi.adminCreateResource(payload);
        alert('Self-Help item created successfully!');
      }

      setShowModal(false);
      fetchAllData();
    } catch (err) {
      alert(err.message || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Item
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await selfHelpApi.adminDeleteResource(id);
      setResources((prev) => prev.filter((r) => (r._id || r.id) !== id));
      alert('Resource deleted successfully.');
    } catch (err) {
      alert(err.message || 'Failed to delete resource');
    }
  };

  // Toggle Publish Status
  const handleTogglePublish = async (id) => {
    try {
      const res = await selfHelpApi.adminTogglePublish(id);
      setResources((prev) =>
        prev.map((r) => ((r._id || r.id) === id ? { ...r, isPublished: res.isPublished } : r))
      );
    } catch (err) {
      alert(err.message || 'Failed to toggle status');
    }
  };

  // Stats
  const stats = {
    total: resources.length,
    audio: resources.filter((r) => r.section === 'audio' || r.type === 'audio').length,
    podcast: resources.filter((r) => r.section === 'podcast' || r.type === 'guide').length,
    activity: resources.filter((r) => r.section === 'activity' || r.type === 'worksheet').length,
    categoriesCount: categories.length,
  };

  // Filtered List
  const filtered = resources.filter((item) => {
    const itemSec = item.section || (item.type === 'worksheet' ? 'activity' : item.type === 'guide' ? 'podcast' : 'audio');
    const matchesSection = activeSectionTab === 'all' || itemSec === activeSectionTab;
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.summary && item.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.narrator && item.narrator.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSection && matchesCategory && matchesSearch;
  });

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 30px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1350px', margin: '0 auto' }}>
          {/* Back to dashboard */}
          <Link
            to="/admin-dashboard"
            style={{ color: '#6366f1', fontWeight: 600, textDecoration: 'none', marginBottom: '16px', display: 'inline-block' }}
          >
            ← Back to Admin Dashboard
          </Link>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                💡 Self-Help Tools Management (Dynamic)
              </h1>
              <p style={{ color: '#64748b', fontSize: '14px', marginTop: '6px', maxWidth: '750px' }}>
                Manage all self-help content dynamically: <strong>Audio Resources</strong> (category-wise folders matching the client UI), <strong>Podcasts</strong> (with PDF companion sheets), and <strong>Activities</strong> (with printable clinical worksheets).
              </p>
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={handleOpenCreateCategoryModal}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13px',
                  border: '1px solid #cbd5e1',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                📁 + New Audio Category
              </button>
              <button
                onClick={() => handleOpenBulkAudioModal()}
                style={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                  color: '#fff',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: 800,
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                🎵 + Upload Multiple Audios
              </button>
              <button
                onClick={() => handleOpenCreateModal('audio')}
                style={{
                  background: '#f5f3ff',
                  color: '#7c3aed',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13px',
                  border: '1.5px solid #d8b4fe',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                🎵 + Single Audio
              </button>
              <button
                onClick={() => handleOpenCreateModal('podcast')}
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#fff',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                🎧 + Add Podcast (PDF)
              </button>
              <button
                onClick={() => handleOpenCreateModal('activity')}
                style={{
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: '#fff',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(5, 150, 105, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                📝 + Upload Worksheet
              </button>
            </div>
          </div>

          {/* 3 Main Sections Navigation Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '28px' }}>
            {/* Audio Section Card */}
            <div
              onClick={() => { setActiveSectionTab('audio'); setFilterCategory('all'); }}
              style={{
                background: '#ffffff',
                padding: '22px',
                borderRadius: '16px',
                border: activeSectionTab === 'audio' ? '2.5px solid #7c3aed' : '1px solid #e2e8f0',
                boxShadow: activeSectionTab === 'audio' ? '0 8px 24px rgba(124,58,237,0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>Section 1: Audio</span>
                <span style={{ fontSize: '24px' }}>🎵</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#7c3aed', marginTop: '6px' }}>{stats.audio} tracks</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                {stats.categoriesCount} dynamic category folders
              </div>
            </div>

            {/* Podcasts Section Card */}
            <div
              onClick={() => { setActiveSectionTab('podcast'); setFilterCategory('all'); }}
              style={{
                background: '#ffffff',
                padding: '22px',
                borderRadius: '16px',
                border: activeSectionTab === 'podcast' ? '2.5px solid #0284c7' : '1px solid #e2e8f0',
                boxShadow: activeSectionTab === 'podcast' ? '0 8px 24px rgba(2,132,199,0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0284c7', textTransform: 'uppercase' }}>Section 2: Podcasts</span>
                <span style={{ fontSize: '24px' }}>🎧</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#0284c7', marginTop: '6px' }}>{stats.podcast} episodes</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>With downloadable PDF materials</div>
            </div>

            {/* Activities Section Card */}
            <div
              onClick={() => { setActiveSectionTab('activity'); setFilterCategory('all'); }}
              style={{
                background: '#ffffff',
                padding: '22px',
                borderRadius: '16px',
                border: activeSectionTab === 'activity' ? '2.5px solid #059669' : '1px solid #e2e8f0',
                boxShadow: activeSectionTab === 'activity' ? '0 8px 24px rgba(5,150,105,0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#059669', textTransform: 'uppercase' }}>Section 3: Activities</span>
                <span style={{ fontSize: '24px' }}>📝</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#059669', marginTop: '6px' }}>{stats.activity} worksheets</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Printable exercises & trackers</div>
            </div>

            {/* Total Resources */}
            <div
              onClick={() => { setActiveSectionTab('all'); setFilterCategory('all'); }}
              style={{
                background: '#ffffff',
                padding: '22px',
                borderRadius: '16px',
                border: activeSectionTab === 'all' ? '2.5px solid #6366f1' : '1px solid #e2e8f0',
                boxShadow: activeSectionTab === 'all' ? '0 8px 24px rgba(99,102,241,0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>All Items</span>
                <span style={{ fontSize: '24px' }}>🌟</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>{stats.total} total</div>
              <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>All sections combined</div>
            </div>
          </div>

          {/* DYNAMIC CATEGORY FOLDERS LIST IN AUDIO SECTION (MIRRORING THE FRONTEND & SCREENSHOT) */}
          {activeSectionTab === 'audio' && (
            <div
              style={{
                background: '#ffffff',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                marginBottom: '28px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              }}
            >
              {/* Table Header */}
              <div
                style={{
                  padding: '16px 24px',
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: 800, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📁 Audio Category Folders (Matching Client Interface)
                  </h3>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Click a category to filter tracks below, or click "+ New Audio Category" to add a new folder to MongoDB.
                  </div>
                </div>

                {filterCategory !== 'all' && (
                  <button
                    onClick={() => setFilterCategory('all')}
                    style={{ background: '#ede9fe', color: '#7c3aed', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✕ Reset Filter (Showing: "{filterCategory}")
                  </button>
                )}
              </div>

              {/* Header "Name" Row as in the image */}
              <div
                style={{
                  padding: '12px 24px',
                  borderBottom: '1.5px solid #d1d5db',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#1f2937',
                  background: '#ffffff',
                }}
              >
                Name
              </div>

              {/* Dynamic folder rows */}
              <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                {categories.map((cat, idx) => {
                  const isEven = idx % 2 === 1;
                  const rowBg = filterCategory === cat.name ? '#ede9fe' : isEven ? '#f9fafb' : '#ffffff';
                  const count = resources.filter(
                    (r) => (r.section === 'audio' || r.type === 'audio') && r.category?.trim().toLowerCase() === cat.name?.trim().toLowerCase()
                  ).length;
                  const hasCustomCover = Boolean(cat.imageUrl || cat.thumbnailUrl);
                  const previewImg = hasCustomCover
                    ? getMediaUrl(cat.imageUrl || cat.thumbnailUrl)
                    : getDefaultCategoryPreview(cat.name, idx);

                  return (
                    <div
                      key={cat._id || cat.name}
                      onClick={() => setFilterCategory(filterCategory === cat.name ? 'all' : cat.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 24px',
                        background: rowBg,
                        borderBottom: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'background 0.15s ease',
                        gap: '12px',
                      }}
                      onMouseEnter={(e) => {
                        if (filterCategory !== cat.name) e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        if (filterCategory !== cat.name) e.currentTarget.style.backgroundColor = rowBg;
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <img
                          src={previewImg}
                          alt={cat.name}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/assets/images/auth-soothing-wellness.jpg';
                          }}
                          style={{
                            width: '48px',
                            height: '38px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: hasCustomCover ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                            flexShrink: 0,
                          }}
                        />
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: filterCategory === cat.name ? '#6d28d9' : '#111827' }}>
                              {cat.name}
                            </span>
                            {hasCustomCover && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  background: '#ede9fe',
                                  color: '#6d28d9',
                                  padding: '1px 6px',
                                  borderRadius: '6px',
                                }}
                              >
                                Custom Cover
                              </span>
                            )}
                          </div>
                          {cat.description && (
                            <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '1px' }}>
                              {cat.description}
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditCategoryModal(cat, idx);
                          }}
                          title={`Set or change cover image for "${cat.name}"`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#f8fafc',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            padding: '4px 9px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          🖼️ Edit Cover
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenBulkAudioModal(cat.name);
                          }}
                          title={`Upload multiple audios into "${cat.name}"`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: filterCategory === cat.name ? '#ffffff' : '#7c3aed',
                            color: filterCategory === cat.name ? '#6d28d9' : '#ffffff',
                            border: filterCategory === cat.name ? '1px solid #c4b5fd' : 'none',
                            borderRadius: '6px',
                            padding: '4px 9px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                          }}
                        >
                          ➕ Upload Audios
                        </button>
                        <span
                          style={{
                            background: filterCategory === cat.name ? '#7c3aed' : '#e5e7eb',
                            color: filterCategory === cat.name ? '#ffffff' : '#4b5563',
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '12px',
                          }}
                        >
                          {count} {count === 1 ? 'audio' : 'audios'}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: '14px' }}>›</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Search & Section Filter Bar */}
          <div
            style={{
              background: '#ffffff',
              padding: '16px 20px',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              gap: '14px',
              flexWrap: 'wrap',
              alignItems: 'center',
              border: '1px solid #e2e8f0',
            }}
          >
            {/* Search Input */}
            <input
              type="text"
              placeholder="🔍 Search items by title, summary, narrator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: '260px',
                padding: '10px 14px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
              }}
            />

            {/* Quick Section Tabs */}
            <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '8px', gap: '4px' }}>
              <button
                type="button"
                onClick={() => { setActiveSectionTab('audio'); setFilterCategory('all'); }}
                style={{
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: activeSectionTab === 'audio' ? '#ffffff' : 'transparent',
                  color: activeSectionTab === 'audio' ? '#7c3aed' : '#64748b',
                  boxShadow: activeSectionTab === 'audio' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                🎵 Audios ({stats.audio})
              </button>
              <button
                type="button"
                onClick={() => { setActiveSectionTab('podcast'); setFilterCategory('all'); }}
                style={{
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: activeSectionTab === 'podcast' ? '#ffffff' : 'transparent',
                  color: activeSectionTab === 'podcast' ? '#0284c7' : '#64748b',
                  boxShadow: activeSectionTab === 'podcast' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                🎧 Podcasts ({stats.podcast})
              </button>
              <button
                type="button"
                onClick={() => { setActiveSectionTab('activity'); setFilterCategory('all'); }}
                style={{
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: activeSectionTab === 'activity' ? '#ffffff' : 'transparent',
                  color: activeSectionTab === 'activity' ? '#059669' : '#64748b',
                  boxShadow: activeSectionTab === 'activity' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                📝 Activities ({stats.activity})
              </button>
              <button
                type="button"
                onClick={() => { setActiveSectionTab('all'); setFilterCategory('all'); }}
                style={{
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: activeSectionTab === 'all' ? '#ffffff' : 'transparent',
                  color: activeSectionTab === 'all' ? '#0f172a' : '#64748b',
                  boxShadow: activeSectionTab === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                All ({stats.total})
              </button>
            </div>
          </div>

          {/* Resources Table */}
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: '12px' }}>
              Loading dynamic resources...
            </div>
          ) : error ? (
            <div style={{ padding: '20px', background: '#fef2f2', color: '#ef4444', borderRadius: '8px' }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '50px 20px', textAlign: 'center', background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', color: '#64748b' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📂</div>
              <h3 style={{ fontSize: '18px', color: '#1e293b', marginBottom: '6px' }}>No items found in this view</h3>
              <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
                Upload an audio file, podcast episode with PDF, or worksheet activity using the buttons above.
              </p>
            </div>
          ) : (
            <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 700 }}>
                    <th style={{ padding: '14px 18px' }}>Title & Description</th>
                    <th style={{ padding: '14px 18px' }}>Section</th>
                    <th style={{ padding: '14px 18px' }}>Category</th>
                    <th style={{ padding: '14px 18px' }}>Media / Attachments</th>
                    <th style={{ padding: '14px 18px' }}>Duration / Audience</th>
                    <th style={{ padding: '14px 18px' }}>Status</th>
                    <th style={{ padding: '14px 18px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => {
                    const id = item._id || item.id;
                    const sec = item.section || (item.type === 'worksheet' ? 'activity' : item.type === 'guide' ? 'podcast' : 'audio');

                    return (
                      <tr key={id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        {/* Title & Description */}
                        <td style={{ padding: '14px 18px', maxWidth: '320px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {item.thumbnailUrl || item.imageUrl ? (
                              <img
                                src={getMediaUrl(item.thumbnailUrl || item.imageUrl)}
                                alt={item.title}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = '/assets/images/auth-soothing-wellness.jpg';
                                }}
                                style={{
                                  width: '48px',
                                  height: '42px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  border: '1.5px solid #c4b5fd',
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '42px',
                                  height: '42px',
                                  borderRadius: '8px',
                                  background: sec === 'audio' ? '#ede9fe' : sec === 'podcast' ? '#e0f2fe' : '#dcfce7',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '20px',
                                  flexShrink: 0,
                                }}
                              >
                                {sec === 'audio' ? '🎵' : sec === 'podcast' ? '🎧' : '📝'}
                              </div>
                            )}
                            <div style={{ overflow: 'hidden' }}>
                              <div style={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {item.title}
                              </div>
                              {item.summary && (
                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.summary}
                                </div>
                              )}
                              {item.narrator && (
                                <div style={{ fontSize: '11px', color: '#8b5cf6', marginTop: '2px', fontWeight: 600 }}>
                                  👤 {item.narrator}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Section */}
                        <td style={{ padding: '14px 18px' }}>
                          {sec === 'audio' ? (
                            <span style={{ background: '#ede9fe', color: '#6d28d9', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>
                              🎵 Audio
                            </span>
                          ) : sec === 'podcast' ? (
                            <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>
                              🎧 Podcast
                            </span>
                          ) : (
                            <span style={{ background: '#dcfce7', color: '#15803d', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>
                              📝 Activity
                            </span>
                          )}
                        </td>

                        {/* Category */}
                        <td style={{ padding: '14px 18px' }}>
                          <span style={{ background: '#f1f5f9', color: '#334155', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            📁 {item.category}
                          </span>
                        </td>

                        {/* Media / Attachments */}
                        <td style={{ padding: '14px 18px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {/* Audio file */}
                            {(item.audioUrl || (sec === 'audio' && item.mediaUrl)) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button
                                  type="button"
                                  onClick={() => setPlayingAudioId(playingAudioId === id ? null : id)}
                                  style={{
                                    background: playingAudioId === id ? '#7c3aed' : '#ede9fe',
                                    color: playingAudioId === id ? '#ffffff' : '#6d28d9',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  {playingAudioId === id ? '⏸ Pause' : '▶ Play Audio'}
                                </button>
                              </div>
                            )}

                            {/* Inline Audio Player */}
                            {playingAudioId === id && (
                              <div style={{ marginTop: '4px' }}>
                                <audio
                                  controls
                                  autoPlay
                                  src={getMediaUrl(item.audioUrl || item.mediaUrl)}
                                  style={{ height: '30px', width: '220px' }}
                                />
                              </div>
                            )}

                            {/* PDF Attachment (for podcast) */}
                            {item.pdfUrl ? (
                              <a
                                href={getMediaUrl(item.pdfUrl)}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  background: '#fef2f2',
                                  color: '#dc2626',
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  textDecoration: 'none',
                                  width: 'fit-content',
                                }}
                              >
                                📄 PDF Guide ({item.pdfFileName || 'Attached'})
                              </a>
                            ) : sec === 'podcast' ? (
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>PDF not uploaded</span>
                            ) : null}

                            {/* Worksheet File (for activity) */}
                            {item.worksheetFileUrl ? (
                              <a
                                href={getMediaUrl(item.worksheetFileUrl)}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  background: '#f0fdf4',
                                  color: '#16a34a',
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  textDecoration: 'none',
                                  width: 'fit-content',
                                }}
                              >
                                📝 Worksheet ({item.worksheetFileName || 'Download'})
                              </a>
                            ) : sec === 'activity' ? (
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>Worksheet not uploaded</span>
                            ) : null}
                          </div>
                        </td>

                        {/* Duration / Audience */}
                        <td style={{ padding: '14px 18px', color: '#475569', fontSize: '13px' }}>
                          {item.duration || item.readTime ? (
                            <div>⏱ {item.duration || item.readTime}</div>
                          ) : null}
                          {sec === 'activity' && item.targetAudience && (
                            <div style={{ fontSize: '11px', color: '#059669', fontWeight: 600, marginTop: '2px' }}>
                              👥 {item.targetAudience}
                            </div>
                          )}
                        </td>

                        {/* Status Toggle */}
                        <td style={{ padding: '14px 18px' }}>
                          <button
                            type="button"
                            onClick={() => handleTogglePublish(id)}
                            style={{
                              border: 'none',
                              borderRadius: '20px',
                              padding: '5px 12px',
                              fontSize: '12px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              background: item.isPublished ? '#dcfce7' : '#fee2e2',
                              color: item.isPublished ? '#15803d' : '#b91c1c',
                            }}
                          >
                            {item.isPublished ? '● Published' : '○ Draft'}
                          </button>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(item)}
                            style={{
                              marginRight: '8px',
                              background: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(id, item.title)}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: 600,
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* CREATE / EDIT AUDIO CATEGORY MODAL (WITH COVER IMAGE BACKEND SUPPORT) */}
      {showCategoryModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '580px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '26px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                {editingCategory ? `🖼️ Edit Category & Cover Image` : '📁 Add New Audio Category'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCategorySubmit}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                Category Folder Name *
              </label>
              <input
                type="text"
                required
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g. Somatic Breathwork or Morning Grounding"
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', marginBottom: '14px' }}
              />

              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                Category Subtitle / Tagline (Optional)
              </label>
              <input
                type="text"
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                placeholder="e.g. Drift into deep, restorative rest with soothing nightscapes"
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', marginBottom: '16px' }}
              />

              {/* Cover Image Input & Upload */}
              <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '14px', marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: '#5b21b6', marginBottom: '6px' }}>
                  🖼️ Category Card Cover Image (Frontend Audio Sanctuary UI)
                </label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={newCategoryImageUrl}
                    onChange={(e) => setNewCategoryImageUrl(e.target.value)}
                    placeholder="Paste image URL or upload an image file..."
                    style={{ flex: 1, padding: '9px 12px', borderRadius: '8px', border: '1px solid #c4b5fd', fontSize: '13px', background: '#fff' }}
                  />
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '9px 14px',
                      borderRadius: '8px',
                      background: '#7c3aed',
                      color: '#ffffff',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <input type="file" accept="image/*" disabled={uploadingCategoryImage} onChange={handleCategoryImageUpload} style={{ display: 'none' }} />
                    {uploadingCategoryImage ? '⏳ Uploading...' : '📤 Upload Image'}
                  </label>
                </div>

                {/* Quick Preset Gallery */}
                <div style={{ fontSize: '11.5px', color: '#6d28d9', fontWeight: 700, marginBottom: '6px' }}>
                  Or click a preset wellness cover image:
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {PRESET_AUDIO_IMAGES.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setNewCategoryImageUrl(preset.url)}
                      style={{
                        padding: '4px 9px',
                        borderRadius: '6px',
                        border: newCategoryImageUrl === preset.url ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                        background: newCategoryImageUrl === preset.url ? '#ede9fe' : '#ffffff',
                        color: '#1e293b',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                {/* Live Cover Preview */}
                {newCategoryImageUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#ffffff', padding: '8px 12px', borderRadius: '10px', border: '1px solid #ddd6fe' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img
                        src={getMediaUrl(newCategoryImageUrl)}
                        alt="Category Cover Preview"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/assets/images/auth-soothing-wellness.jpg';
                        }}
                        style={{ width: '76px', height: '48px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 700, color: '#059669' }}>✓ Cover Image Active</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewCategoryImageUrl('')}
                      style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                    >
                      ✕ Clear
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  style={{ padding: '8px 16px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingCategory}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '8px',
                    background: '#7c3aed',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#ffffff',
                    cursor: creatingCategory ? 'not-allowed' : 'pointer',
                  }}
                >
                  {creatingCategory ? 'Saving...' : editingCategory ? 'Save Category Changes' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BULK UPLOAD AUDIO MODAL */}
      {showBulkAudioModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '860px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '28px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '24px' }}>🎵</span>
                  <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                    Upload Multiple Audio Tracks
                  </h2>
                </div>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                  Select and upload multiple audio tracks at once directly under any audio category folder.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowBulkAudioModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {bulkError && (
              <div
                style={{
                  background: '#fee2e2',
                  border: '1px solid #fca5a5',
                  color: '#991b1b',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                ⚠️ {bulkError}
              </div>
            )}

            <form onSubmit={handleBulkSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Category Selection */}
              <div style={{ background: '#f5f3ff', border: '1.5px solid #d8b4fe', borderRadius: '12px', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 700, color: '#5b21b6' }}>
                    📁 Target Audio Category Folder *
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsNewBulkCategory(!isNewBulkCategory);
                      setNewBulkCategoryName('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#7c3aed',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    {isNewBulkCategory ? '← Choose Existing Category' : '➕ Or Create New Category'}
                  </button>
                </div>

                {!isNewBulkCategory ? (
                  <select
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid #c4b5fd',
                      fontSize: '14px',
                      fontWeight: 600,
                      background: '#fff',
                      color: '#1e293b',
                    }}
                  >
                    {categories.length > 0 ? (
                      categories.map((c) => (
                        <option key={c._id || c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="Mindfulness & Relaxation">Mindfulness & Relaxation</option>
                        <option value="Stress & Anxiety Relief">Stress & Anxiety Relief</option>
                        <option value="Deep Sleep & Rest">Deep Sleep & Rest</option>
                        <option value="Focus & Productivity">Focus & Productivity</option>
                      </>
                    )}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={newBulkCategoryName}
                    onChange={(e) => setNewBulkCategoryName(e.target.value)}
                    placeholder="Enter new audio category name (e.g. Daily Affirmations)"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1.5px solid #7c3aed',
                      fontSize: '14px',
                      background: '#fff',
                    }}
                  />
                )}
              </div>

              {/* Default Speaker & Description */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Narrator / Speaker Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={bulkNarrator}
                    onChange={(e) => {
                      const val = e.target.value;
                      setBulkNarrator(val);
                      setBulkTracks((prev) =>
                        prev.map((t) => (!t.narratorModified ? { ...t, narrator: val } : t))
                      );
                    }}
                    placeholder="e.g. Dr. Sarah Mitchell"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>Applies as default to all uploaded tracks</div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    Category Overview / Summary (Optional)
                  </label>
                  <input
                    type="text"
                    value={bulkSummary}
                    onChange={(e) => setBulkSummary(e.target.value)}
                    placeholder="e.g. Guided relaxation audio sessions for daily peace"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>Default description for tracks in this batch</div>
                </div>
              </div>

              {/* Cover Image for Uploaded Audios */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  🖼️ Cover Image for Audio Cards (Optional — Upload or Paste URL)
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={bulkThumbnailUrl}
                    onChange={(e) => setBulkThumbnailUrl(e.target.value)}
                    placeholder="Paste cover image URL or upload an image file..."
                    style={{ flex: 1, minWidth: '220px', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                  />
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '9px 14px',
                      borderRadius: '8px',
                      background: '#ede9fe',
                      color: '#6d28d9',
                      border: '1px solid #c4b5fd',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    <input type="file" accept="image/*" disabled={uploadingBulkThumbnail} onChange={handleBulkThumbnailUpload} style={{ display: 'none' }} />
                    {uploadingBulkThumbnail ? '⏳ Uploading...' : '📤 Upload Cover Image'}
                  </label>
                  {bulkThumbnailUrl && (
                    <img
                      src={getMediaUrl(bulkThumbnailUrl)}
                      alt="Cover Preview"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/assets/images/auth-soothing-wellness.jpg';
                      }}
                      style={{ width: '58px', height: '38px', borderRadius: '6px', objectFit: 'cover', border: '1.5px solid #7c3aed' }}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {PRESET_AUDIO_IMAGES.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setBulkThumbnailUrl(preset.url)}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        border: bulkThumbnailUrl === preset.url ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                        background: bulkThumbnailUrl === preset.url ? '#ede9fe' : '#ffffff',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Multiple Audio Upload Drop Area */}
              <div>
                <label
                  htmlFor="bulkAudioFileInput"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '28px 20px',
                    background: '#f8fafc',
                    border: '2px dashed #7c3aed',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f3ff')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '#f8fafc')}
                >
                  <input
                    id="bulkAudioFileInput"
                    type="file"
                    multiple
                    accept="audio/*,.mp3,.wav,.m4a"
                    onChange={handleBulkAudioFilesSelected}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: '36px', marginBottom: '6px' }}>🎙️</span>
                  <span style={{ fontSize: '15px', fontWeight: 800, color: '#5b21b6' }}>
                    Click or Drag to Upload Multiple Audio Files
                  </span>
                  <span style={{ fontSize: '12px', color: '#6d28d9', marginTop: '4px' }}>
                    Hold Ctrl / Cmd to select 5, 10, or more MP3, WAV, or M4A audio files at once
                  </span>
                </label>
              </div>

              {/* Uploaded Audio Tracks List */}
              {bulkTracks.length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>
                      Tracks to Upload ({bulkTracks.length})
                      <span style={{ marginLeft: '10px', fontSize: '12px', fontWeight: 600, color: '#16a34a' }}>
                        {bulkTracks.filter((t) => t.status === 'done').length} ready
                      </span>
                      {bulkTracks.some((t) => t.status === 'uploading') && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 600, color: '#d97706' }}>
                          • {bulkTracks.filter((t) => t.status === 'uploading').length} uploading...
                        </span>
                      )}
                    </div>
                    <label
                      htmlFor="bulkAudioFileInputMore"
                      style={{
                        background: '#ede9fe',
                        color: '#6d28d9',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      + Add More Files
                      <input
                        id="bulkAudioFileInputMore"
                        type="file"
                        multiple
                        accept="audio/*,.mp3,.wav,.m4a"
                        onChange={handleBulkAudioFilesSelected}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>

                  <div
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      maxHeight: '340px',
                      overflowY: 'auto',
                      background: '#fff',
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#64748b' }}>
                          <th style={{ padding: '8px 12px', width: '36px' }}>#</th>
                          <th style={{ padding: '8px 12px' }}>Track Title *</th>
                          <th style={{ padding: '8px 12px', width: '100px' }}>Duration</th>
                          <th style={{ padding: '8px 12px', width: '180px' }}>Preview / Status</th>
                          <th style={{ padding: '8px 12px', width: '40px', textAlign: 'center' }}>✕</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkTracks.map((track, idx) => (
                          <tr key={track.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 700 }}>
                              {idx + 1}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input
                                type="text"
                                value={track.title}
                                onChange={(e) => handleUpdateBulkTrack(track.id, 'title', e.target.value)}
                                placeholder="Track Title"
                                style={{
                                  width: '100%',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                }}
                              />
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
                                📁 {track.fileName}
                              </div>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input
                                type="text"
                                value={track.duration}
                                onChange={(e) => handleUpdateBulkTrack(track.id, 'duration', e.target.value)}
                                placeholder="e.g. 5:30"
                                style={{
                                  width: '100%',
                                  padding: '6px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '12px',
                                }}
                              />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {track.status === 'uploading' && (
                                <span style={{ color: '#d97706', fontSize: '12px', fontWeight: 700 }}>
                                  ⏳ Uploading...
                                </span>
                              )}
                              {track.status === 'error' && (
                                <span style={{ color: '#dc2626', fontSize: '12px', fontWeight: 700 }} title={track.errorMsg}>
                                  ❌ Upload failed
                                </span>
                              )}
                              {track.status === 'done' && track.audioUrl && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <audio
                                    controls
                                    src={getMediaUrl(track.audioUrl)}
                                    style={{ height: '30px', maxWidth: '170px' }}
                                  />
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleRemoveBulkTrack(track.id)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  fontSize: '16px',
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                }}
                                title="Remove this track"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setShowBulkAudioModal(false)}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#475569',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    bulkSaving ||
                    bulkTracks.length === 0 ||
                    bulkTracks.some((t) => t.status === 'uploading')
                  }
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    background:
                      bulkSaving ||
                      bulkTracks.length === 0 ||
                      bulkTracks.some((t) => t.status === 'uploading')
                        ? '#cbd5e1'
                        : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: 800,
                    color: '#ffffff',
                    cursor:
                      bulkSaving ||
                      bulkTracks.length === 0 ||
                      bulkTracks.some((t) => t.status === 'uploading')
                        ? 'not-allowed'
                        : 'pointer',
                    boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
                  }}
                >
                  {bulkSaving
                    ? 'Saving All Audios...'
                    : `Save All (${bulkTracks.filter((t) => t.status === 'done').length}) Audios to "${isNewBulkCategory ? newBulkCategoryName || 'New Category' : bulkCategory}"`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CREATE / EDIT RESOURCE MODAL */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '820px',
              width: '100%',
              maxHeight: '92vh',
              overflowY: 'auto',
              padding: '28px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  {editingItem ? '✏️ Edit Self-Help Resource' : '➕ Create New Self-Help Tool'}
                </h2>
                <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                  Select the target section to configure specialized upload fields.
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>

            {/* 3 Main Sections Switcher */}
            <div style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>
                Select Self-Help Section *
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div
                  onClick={() => handleSectionChange('audio')}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: formData.section === 'audio' ? '2px solid #7c3aed' : '1px solid #cbd5e1',
                    background: formData.section === 'audio' ? '#f5f3ff' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '26px' }}>🎵</div>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: formData.section === 'audio' ? '#7c3aed' : '#1e293b', marginTop: '4px' }}>
                    1. Audio Resources
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Category-wise upload</div>
                </div>

                <div
                  onClick={() => handleSectionChange('podcast')}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: formData.section === 'podcast' ? '2px solid #0284c7' : '1px solid #cbd5e1',
                    background: formData.section === 'podcast' ? '#f0f9ff' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '26px' }}>🎧</div>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: formData.section === 'podcast' ? '#0284c7' : '#1e293b', marginTop: '4px' }}>
                    2. Podcasts
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>With PDF guide upload</div>
                </div>

                <div
                  onClick={() => handleSectionChange('activity')}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: formData.section === 'activity' ? '2px solid #059669' : '1px solid #cbd5e1',
                    background: formData.section === 'activity' ? '#f0fdf4' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '26px' }}>📝</div>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: formData.section === 'activity' ? '#059669' : '#1e293b', marginTop: '4px' }}>
                    3. Activities & Worksheets
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Worksheet file upload</div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              {/* Title & Category Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    {formData.section === 'audio'
                      ? 'Audio Track Title *'
                      : formData.section === 'podcast'
                        ? 'Podcast Episode Title *'
                        : 'Activity / Worksheet Title *'}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter resource title..."
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                  />
                </div>

                {/* Category Selection */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                    {formData.section === 'audio' ? 'Select Audio Category *' : 'Category / Topic *'}
                  </label>

                  {formData.section === 'audio' ? (
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', background: '#fff' }}
                    >
                      {categories.map((c) => (
                        <option key={c._id || c.name} value={c.name}>
                          📁 {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g. CBT Reflection, Mindfulness, Burnout..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                    />
                  )}
                </div>
              </div>

              {/* SECTION 1: AUDIO UPLOAD */}
              {formData.section === 'audio' && (
                <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '12px', padding: '18px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#6d28d9', margin: '0 0 12px 0' }}>
                    🎵 Audio File Upload
                  </h3>

                  <div style={{ marginBottom: '14px' }}>
                    {formData.audioUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #c4b5fd' }}>
                        <div style={{ flex: 1 }}>
                          <audio controls src={getMediaUrl(formData.audioUrl)} style={{ width: '100%', height: '36px' }} />
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, audioUrl: '' }))}
                          style={{ marginLeft: '12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    ) : (
                      <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '22px', background: '#fff', border: '2px dashed #a78bfa', borderRadius: '8px', cursor: 'pointer', textAlign: 'center' }}>
                        <input type="file" accept="audio/*,.mp3,.wav,.m4a" disabled={uploadingAudio} onChange={handleAudioUpload} style={{ display: 'none' }} />
                        <span style={{ fontSize: '28px', marginBottom: '4px' }}>{uploadingAudio ? '⏳' : '🎙️'}</span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#5b21b6' }}>
                          {uploadingAudio ? 'Uploading audio...' : 'Click to Upload Audio Track'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#6d28d9', marginTop: '2px' }}>MP3, WAV, M4A up to 200MB</span>
                      </label>
                    )}
                    {audioUploadError && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{audioUploadError}</div>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#4c1d95', marginBottom: '4px' }}>
                        Audio Duration
                      </label>
                      <input
                        type="text"
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        placeholder="e.g. 5:30 or 10 mins"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#4c1d95', marginBottom: '4px' }}>
                        Narrator / Therapist Name
                      </label>
                      <input
                        type="text"
                        value={formData.narrator}
                        onChange={(e) => setFormData({ ...formData, narrator: e.target.value })}
                        placeholder="e.g. Dr. Sarah Mitchell"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                      />
                    </div>
                  </div>

                  {/* Audio Card Cover Image Input & Upload */}
                  <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #ddd6fe' }}>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#4c1d95', marginBottom: '6px' }}>
                      🖼️ Audio Card Cover Image (Displayed on Self-Help Audios UI)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={formData.thumbnailUrl}
                        onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                        placeholder="Paste cover image URL or upload an image file..."
                        style={{ flex: 1, minWidth: '220px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #c4b5fd', fontSize: '13px', background: '#fff' }}
                      />
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '6px',
                          background: '#7c3aed',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <input type="file" accept="image/*" disabled={uploadingThumbnail} onChange={handleThumbnailUpload} style={{ display: 'none' }} />
                        {uploadingThumbnail ? '⏳ Uploading...' : '📤 Upload Cover Image'}
                      </label>
                    </div>

                    {/* Quick Preset Wellness Images */}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: formData.thumbnailUrl ? '10px' : '0' }}>
                      {PRESET_AUDIO_IMAGES.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => setFormData({ ...formData, thumbnailUrl: preset.url })}
                          style={{
                            padding: '3px 8px',
                            borderRadius: '6px',
                            border: formData.thumbnailUrl === preset.url ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                            background: formData.thumbnailUrl === preset.url ? '#ede9fe' : '#ffffff',
                            color: '#1e293b',
                            fontSize: '11px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>

                    {thumbnailUploadError && (
                      <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{thumbnailUploadError}</div>
                    )}

                    {formData.thumbnailUrl && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          background: '#ffffff',
                          borderRadius: '8px',
                          border: '1px solid #c4b5fd',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <img
                            src={getMediaUrl(formData.thumbnailUrl)}
                            alt="Audio Cover Preview"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = '/assets/images/auth-soothing-wellness.jpg';
                            }}
                            style={{ width: '72px', height: '46px', borderRadius: '6px', objectFit: 'cover' }}
                          />
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#059669' }}>
                            ✓ Custom Audio Cover Image Ready
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, thumbnailUrl: '' }))}
                          style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* SECTION 2: PODCAST UPLOAD (WITH PDF) */}
              {formData.section === 'podcast' && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', padding: '18px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#0369a1', margin: '0 0 12px 0' }}>
                    🎧 Podcast Episode & PDF Materials Upload
                  </h3>

                  {/* PDF Upload */}
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#075985', marginBottom: '6px' }}>
                      📄 Upload Episode PDF / Notes (Requested)
                    </label>

                    {formData.pdfUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #7dd3fc' }}>
                        <div>
                          <a href={getMediaUrl(formData.pdfUrl)} target="_blank" rel="noreferrer" style={{ fontSize: '13px', fontWeight: 700, color: '#0284c7', textDecoration: 'underline' }}>
                            {formData.pdfFileName || 'Episode-Guide.pdf'}
                          </a>
                          <div style={{ fontSize: '11px', color: '#16a34a' }}>✓ PDF Document ready for client download</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, pdfUrl: '', pdfFileName: '' }))}
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    ) : (
                      <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: '#fff', border: '2px dashed #38bdf8', borderRadius: '8px', cursor: 'pointer', textAlign: 'center' }}>
                        <input type="file" accept="application/pdf,.pdf" disabled={uploadingPdf} onChange={handlePdfUpload} style={{ display: 'none' }} />
                        <span style={{ fontSize: '28px', marginBottom: '4px' }}>{uploadingPdf ? '⏳' : '📑'}</span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#0369a1' }}>
                          {uploadingPdf ? 'Uploading PDF...' : 'Click to Upload Episode PDF Document'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#0284c7', marginTop: '2px' }}>Upload study guide, summary notes, or transcript (.pdf)</span>
                      </label>
                    )}
                    {pdfUploadError && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{pdfUploadError}</div>}
                  </div>

                  {/* Audio Upload for Podcast */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#075985', marginBottom: '4px' }}>
                        Upload Audio File (Optional)
                      </label>
                      <input type="file" accept="audio/*,.mp3,.wav,.m4a" onChange={handleAudioUpload} style={{ width: '100%', fontSize: '12px' }} />
                      {formData.audioUrl && <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px' }}>✓ Audio file ready</div>}
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#075985', marginBottom: '4px' }}>
                        Host / Speaker Name
                      </label>
                      <input
                        type="text"
                        value={formData.narrator}
                        onChange={(e) => setFormData({ ...formData, narrator: e.target.value })}
                        placeholder="e.g. Dr. Ananya Sharma"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                      />
                    </div>
                  </div>

                  {/* Podcast Card Cover Image Input & Upload */}
                  <div style={{ paddingTop: '12px', borderTop: '1px solid #bae6fd' }}>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#075985', marginBottom: '6px' }}>
                      🖼️ Podcast Episode Cover Image (Optional — Upload or Paste URL)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={formData.thumbnailUrl}
                        onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                        placeholder="Paste cover image URL or upload an image file..."
                        style={{ flex: 1, minWidth: '220px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #7dd3fc', fontSize: '13px', background: '#fff' }}
                      />
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '6px',
                          background: '#0284c7',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        <input type="file" accept="image/*" disabled={uploadingThumbnail} onChange={handleThumbnailUpload} style={{ display: 'none' }} />
                        {uploadingThumbnail ? '⏳ Uploading...' : '📤 Upload Cover Image'}
                      </label>
                      {formData.thumbnailUrl && (
                        <img
                          src={getMediaUrl(formData.thumbnailUrl)}
                          alt="Podcast Cover Preview"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/assets/images/blog/blog-1-1.jpg';
                          }}
                          style={{ width: '64px', height: '42px', borderRadius: '6px', objectFit: 'cover', border: '1.5px solid #0284c7' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* SECTION 3: ACTIVITIES & WORKSHEETS */}
              {formData.section === 'activity' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '18px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 800, color: '#15803d', margin: '0 0 12px 0' }}>
                    📝 Worksheet Upload & Activity Details
                  </h3>

                  {/* Worksheet Upload */}
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#166534', marginBottom: '6px' }}>
                      Upload Printable Worksheet File (.pdf, .docx, .doc, .xlsx) *
                    </label>

                    {formData.worksheetFileUrl ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #86efac' }}>
                        <div>
                          <a href={getMediaUrl(formData.worksheetFileUrl)} target="_blank" rel="noreferrer" style={{ fontSize: '13px', fontWeight: 700, color: '#15803d', textDecoration: 'underline' }}>
                            {formData.worksheetFileName || 'Worksheet.pdf'}
                          </a>
                          <div style={{ fontSize: '11px', color: '#16a34a' }}>✓ Worksheet document ready for user download</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, worksheetFileUrl: '', worksheetFileName: '' }))}
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          ✕ Remove
                        </button>
                      </div>
                    ) : (
                      <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', background: '#fff', border: '2px dashed #4ade80', borderRadius: '8px', cursor: 'pointer', textAlign: 'center' }}>
                        <input type="file" accept=".pdf,.doc,.docx,.xlsx,application/pdf" disabled={uploadingWorksheet} onChange={handleWorksheetUpload} style={{ display: 'none' }} />
                        <span style={{ fontSize: '28px', marginBottom: '4px' }}>{uploadingWorksheet ? '⏳' : '📥'}</span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#15803d' }}>
                          {uploadingWorksheet ? 'Uploading worksheet...' : 'Click to Upload Printable Worksheet File'}
                        </span>
                        <span style={{ fontSize: '11px', color: '#16a34a', marginTop: '2px' }}>PDF, DOCX, XLSX printable worksheets</span>
                      </label>
                    )}
                    {worksheetUploadError && <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>{worksheetUploadError}</div>}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#166534', marginBottom: '4px' }}>
                        Target Audience
                      </label>
                      <select
                        value={formData.targetAudience}
                        onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
                      >
                        {TARGET_AUDIENCES.map((aud) => (
                          <option key={aud} value={aud}>
                            {aud}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#166534', marginBottom: '4px' }}>
                        Exercise Instructions
                      </label>
                      <textarea
                        rows={2}
                        value={formData.instructions}
                        onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                        placeholder="Step 1: Download worksheet. Step 2: Record thought triggers..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontFamily: 'inherit' }}
                      />
                    </div>
                  </div>

                  {/* Worksheet Card Cover Image Input & Upload */}
                  <div style={{ paddingTop: '12px', borderTop: '1px solid #bbf7d0' }}>
                    <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#166534', marginBottom: '6px' }}>
                      🖼️ Worksheet Card Cover Image (Optional — Upload or Paste URL)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="text"
                        value={formData.thumbnailUrl}
                        onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                        placeholder="Paste cover image URL or upload an image file..."
                        style={{ flex: 1, minWidth: '220px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #86efac', fontSize: '13px', background: '#fff' }}
                      />
                      <label
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '6px',
                          background: '#059669',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        <input type="file" accept="image/*" disabled={uploadingThumbnail} onChange={handleThumbnailUpload} style={{ display: 'none' }} />
                        {uploadingThumbnail ? '⏳ Uploading...' : '📤 Upload Cover Image'}
                      </label>
                      {formData.thumbnailUrl && (
                        <img
                          src={getMediaUrl(formData.thumbnailUrl)}
                          alt="Worksheet Cover Preview"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/assets/images/blog/blog-1-5.jpg';
                          }}
                          style={{ width: '64px', height: '42px', borderRadius: '6px', objectFit: 'cover', border: '1.5px solid #059669' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Description / Summary */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Overview & Summary *
                </label>
                <textarea
                  rows={3}
                  required
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Provide a concise description of what this self-help tool covers..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', fontFamily: 'inherit' }}
                />
              </div>

              {/* Publish Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="isPublishedCheck"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  style={{ width: '18px', height: '18px', accentColor: '#10b981', cursor: 'pointer' }}
                />
                <label htmlFor="isPublishedCheck" style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', cursor: 'pointer' }}>
                  Publish live to Website Frontend (`/self-help`)
                </label>
              </div>

              {/* Form Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ padding: '10px 18px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    background:
                      formData.section === 'audio'
                        ? '#7c3aed'
                        : formData.section === 'podcast'
                          ? '#0284c7'
                          : '#059669',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#ffffff',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {submitting ? 'Saving...' : editingItem ? 'Save Changes' : 'Create Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminSelfHelpPage;
